// Embeddable contact form bundle entry (in-page rendering, no iframe).
// Registers:
//   <clinic-contact-widget site-id locale base-url clinic-id? turnstile-site-key?>
//
// Renders ONLY the form body — the host page keeps its own headings, intro
// copy and layout. Submissions go to POST /api/public/contact (Turnstile,
// consent ledger, Google Sheets mirror, patient + practice emails, lead
// promotion all happen server-side). The element dispatches composed
// CustomEvents so the host page can wire its own analytics under its own
// consent rules: `contact-start`, `contact-success` (detail incl. email/phone
// for enhanced conversions — stays in-page until the host decides), and
// `contact-error` (detail.code).

import { createRoot, type Root } from 'react-dom/client';
import { StrictMode, useEffect, useState } from 'react';
import { NextIntlClientProvider } from 'next-intl';
// Bundled by esbuild into contact-widget.css (the booking visual system is
// the contact form's stylesheet too — same tokens, same field classes).
import '../../src/components/booking/booking-widget.css';
import { ensureMessages, messagesFor, normalizeLocale } from './messages';
import { dispatchHostEvent, fetchWithTimeout, injectStyles, readHostParams } from './shadow';
import { EmbedLoading, EmbedUnavailable } from './status';
import {
  ContactFormEmbed,
  type ContactSuccessDetail,
} from './contact-form';
import type { Locale } from '../../src/i18n/config';

interface ContactAppProps {
  siteId: string;
  baseUrl: string;
  locale: Locale;
  preselectClinic?: string;
  turnstileSiteKeyAttr?: string;
  onStart: () => void;
  onSuccess: (detail: ContactSuccessDetail) => void;
  onError: (code: string) => void;
}

interface PublicConfig {
  clinics: { id: string; slug: string | null; display_name: string }[];
  embed?: { theme?: string; turnstileSiteKey?: string | null };
}

function ContactEmbedApp(props: ContactAppProps) {
  const [cfg, setCfg] = useState<PublicConfig | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by the failure panel's "try again" to re-run resolution.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchWithTimeout(`${props.baseUrl}/api/public/clinics?site=${encodeURIComponent(props.siteId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: PublicConfig) => {
        if (!cancelled) setCfg(d);
      })
      .catch((err) => {
        console.warn('[clinic-contact-widget] config resolution failed:', err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // Props are stable per mount; attribute changes remount the root.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  if (failed) {
    return (
      <div className="booking-widget booking-widget--bare">
        <EmbedUnavailable
          locale={props.locale}
          kind="form"
          onRetry={() => {
            setFailed(false);
            setAttempt((a) => a + 1);
          }}
        />
      </div>
    );
  }
  if (!cfg) {
    return (
      <div className="booking-widget booking-widget--bare">
        <EmbedLoading locale={props.locale} />
      </div>
    );
  }

  return (
    <ContactFormEmbed
      baseUrl={props.baseUrl}
      locale={props.locale}
      clinics={cfg.clinics}
      preselectClinic={props.preselectClinic}
      turnstileSiteKey={props.turnstileSiteKeyAttr ?? cfg.embed?.turnstileSiteKey ?? undefined}
      theme={cfg.embed?.theme ?? 'halcyon'}
      onStart={props.onStart}
      onSuccess={props.onSuccess}
      onError={props.onError}
    />
  );
}

class ClinicContactWidget extends HTMLElement {
  static observedAttributes = ['site-id', 'clinic-id', 'locale', 'base-url', 'turnstile-site-key'];

  private root: Root | null = null;
  private mounted = false;
  private mountGen = 0;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null) {
    if (!this.mounted) return;
    if (oldValue === newValue) return;
    this.unmount();
    this.connectedCallback();
  }

  disconnectedCallback() {
    this.unmount();
  }

  private unmount() {
    this.mountGen++;
    this.mounted = false;
    try {
      this.root?.unmount();
    } catch {
      // noop
    }
    this.root = null;
    this.shadowRoot?.replaceChildren();
  }

  connectedCallback() {
    if (this.mounted) return;
    this.mounted = true;
    const gen = ++this.mountGen;

    const siteId = this.getAttribute('site-id');
    const baseUrlRaw = this.getAttribute('base-url');
    if (!siteId || !baseUrlRaw) {
      this.renderError('configuration_missing');
      return;
    }
    const baseUrl = baseUrlRaw.replace(/\/$/, '');
    const locale = normalizeLocale(this.getAttribute('locale'));
    const attr = (n: string) => {
      const v = this.getAttribute(n);
      return v && v.trim() !== '' ? v.trim() : undefined;
    };
    const preselectClinic = attr('clinic-id') ?? readHostParams().clinic;

    const shadow = this.shadowRoot!;
    // Styles + locale catalog load in parallel; neither ever rejects (both
    // degrade — unstyled CSS, pt strings — rather than block the mount).
    void Promise.all([
      injectStyles(shadow, baseUrl, 'contact-widget.css'),
      ensureMessages(locale, baseUrl),
    ]).then(() => {
      if (gen !== this.mountGen) return;
      const container = document.createElement('div');
      shadow.appendChild(container);
      this.root = createRoot(container);
      this.root.render(
        <StrictMode>
          <NextIntlClientProvider
            locale={locale}
            timeZone="Europe/Lisbon"
            messages={messagesFor(locale) as never}
          >
            <ContactEmbedApp
              siteId={siteId}
              baseUrl={baseUrl}
              locale={locale}
              preselectClinic={preselectClinic}
              turnstileSiteKeyAttr={attr('turnstile-site-key')}
              onStart={() => dispatchHostEvent(this, 'contact-start')}
              onSuccess={(detail) => dispatchHostEvent(this, 'contact-success', detail)}
              onError={(code) => dispatchHostEvent(this, 'contact-error', { code })}
            />
          </NextIntlClientProvider>
        </StrictMode>,
      );
    });
  }

  private renderError(code: string) {
    const div = document.createElement('div');
    div.style.cssText = 'padding:24px;text-align:center;color:#6b7280;font-family:system-ui;';
    div.textContent = `Contact form unavailable (${code}).`;
    this.shadowRoot?.appendChild(div);
  }
}

if (!customElements.get('clinic-contact-widget')) {
  customElements.define('clinic-contact-widget', ClinicContactWidget);
}

export {};
